import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faPlus,
  faSpinner,
  faCheckCircle,
  faExclamationCircle,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

export default function AddonsManagement() {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [formData, setFormData] = useState({ addonId: "", title: "", description: "" });
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAddons();
  }, []);

  const fetchAddons = async () => {
    try {
      const response = await fetch("/api/addons", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch addons");
      }
      const data = await response.json();
      setAddons(data);
    } catch (error) {
      setError({ type: "error", message: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError(null);
  };

  const openModal = (addon = null) => {
    if (addon) {
      setFormData({ addonId: addon.addonId, title: addon.title, description: addon.description });
      setEditingAddon(addon);
    } else {
      setFormData({ addonId: "", title: "", description: "" });
      setEditingAddon(null);
    }
    setError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ addonId: "", title: "", description: "" });
    setEditingAddon(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.addonId || !formData.title || !formData.description) {
      setError({ type: "error", message: "All fields are required" });
      return;
    }

    setActionLoading(true);
    try {
      const url = editingAddon
        ? `/api/addons/${editingAddon.id}`
        : "/api/addons";
      const method = editingAddon ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to save addon");
      }

      await fetchAddons();
      closeModal();
      setError({
        type: "success",
        message: `Addon successfully ${editingAddon ? "updated" : "created"}`,
      });
      setTimeout(() => setError(null), 3000);
    } catch (error) {
      setError({ type: "error", message: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      setActionLoading(true);
      try {
        const response = await fetch(`/api/addons/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete addon");
        }

        await fetchAddons();
        setError({
          type: "success",
          message: `Addon "${title}" was successfully deleted.`,
        });
        setTimeout(() => setError(null), 3000);
      } catch (error) {
        setError({ type: "error", message: error.message });
      } finally {
        setActionLoading(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading addons...</p>
      </div>
    );
  }

  return (
    <div className="addons-container">
      {error && !isModalOpen && (
        <div className={`message-box ${error.type}`}>
          <span>
            <FontAwesomeIcon
              icon={
                error.type === "success" ? faCheckCircle : faExclamationCircle
              }
            />
            {error.message}
          </span>
          <button onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      <div className="management-header">
        <h2>Addon Management</h2>
        <button
          className="add-btn"
          onClick={() => openModal()}
          disabled={actionLoading}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Addon
        </button>
      </div>

      <div className="addons-list">
        {addons.length === 0 ? (
          <p className="no-data-message">
            No addons found. Click "Add Addon" to create one.
          </p>
        ) : (
          addons.map((addon) => (
            <div key={addon.id} className="addon-card">
              <h3>{addon.title}</h3>
              <p>Addon Id: {addon.addonId}</p>
              <p>Description: {addon.description}</p>
              <div className="actions">
                <button
                  className="btn btn-edit"
                  onClick={() => openModal(addon)}
                  disabled={actionLoading}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button
                  className="btn btn-delete"
                  onClick={() => handleDelete(addon.id, addon.title)}
                  disabled={actionLoading}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingAddon ? "Edit Addon" : "Add New Addon"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <div className="form-group">
                  <label htmlFor="addonId">Addon Id</label>
                  <input
                    type="text"
                    id="addonId"
                    name="addonId"
                    value={formData.addonId}
                    onChange={handleInputChange}
                    disabled={actionLoading}
                  />
                </div>
                <label htmlFor="title">Addon Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  disabled={actionLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <input
                  type="text"
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  disabled={actionLoading}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-cancel"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-save"
                  disabled={actionLoading}
                >
                  {actionLoading
                    ? "Saving..."
                    : editingAddon
                    ? "Update"
                    : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
